<script setup lang="ts">
import { onBeforeUnmount, watch, ref } from 'vue';
import { useRouter } from 'vue-router';
import BankingWrapper from '@/application/layouts/ContentWrapper/BankingWrapper.vue';
import { throwableCall } from '@/api/lib/integration.ts';
import { notifyAccountCreated } from '@/api/banking/banksApi.ts';
import { captureException } from '@sentry/vue';
import { useTagManager } from '@demv_systems/feu-tag-manager';
import { TrackEventCategory } from '@/application/utils/tracking/events';
import { appUrl } from "@/application/utils/envInfo";

interface RegprotectElement extends HTMLElement {
  sessionLink?: string;
  reentryUrl?: string;
  hidePortalButton?: boolean;
  portalButtonLabel?: string;
}

const props = defineProps<{
  sessionLink?: string;
  redirect?: string;
}>();

const router = useRouter();
let wcElement: RegprotectElement | undefined;
// Counts every teardown so an in-flight createWebComponent() stops appending its element
// once a redirect has already started the next one.
let generation = 0;

const regProtectContainerRef = ref<HTMLDivElement>();

/**
 * The REG/Protect bundle ships zone.js, which replaces the global window.Promise with
 * ZoneAwarePromise on load. Loading it here instead of in index.html keeps that patch
 * scoped to the REG/Protect flow and takes ~1 MB off every other page load.
 */
const loadWebComponent = () => import('@banksapitechnology/regprotect-web-component/dist/ba-regprotect.js');

async function handleWebComponentListener(event: Event) {
  const callbackUrl = (event as CustomEvent).detail;
  const url = new URL(callbackUrl);

  const baReentry = url.searchParams.get('baReentry');

  if (baReentry === 'ACCOUNT_CREATED') {
    try {
      await throwableCall(notifyAccountCreated())
      useTagManager().push({ event: TrackEventCategory.BANKS_API, action: 'ACCOUNT_CREATED' });
    } catch (err) {
      captureException(err);
    }
  }

  await router.push(url.pathname);
}

function cleanUpExistingWebComponent() {
  generation += 1;

  if (wcElement) {
    wcElement.removeEventListener('subscribed', handleWebComponentListener);
    wcElement.remove();
    wcElement = undefined;
  }
}

async function createWebComponent(container: HTMLDivElement) {
  const current = generation;

  await loadWebComponent();

  if (current !== generation) return;

  wcElement = document.createElement('ba-regprotect') as RegprotectElement;
  wcElement.sessionLink = props.sessionLink;
  wcElement.reentryUrl = `${appUrl}/banking/regprotect?redirect=${Date.now()}`;
  // Without the portal button the callback fires on its own, saving the user a click at
  // the end of the flow. The EBICS ini-letter page keeps its button as the intended
  // exit, which is where portalButtonLabel applies.
  wcElement.hidePortalButton = true;
  wcElement.portalButtonLabel = 'Zurück zur Finanzübersicht';
  wcElement.addEventListener('subscribed', handleWebComponentListener);
  container.appendChild(wcElement);
}

watch(
  [() => props.redirect, regProtectContainerRef],
  ([_, newRegProtectContainerRef]) => {
    if (newRegProtectContainerRef) {
      cleanUpExistingWebComponent();
      createWebComponent(newRegProtectContainerRef).catch(captureException);
    }
  },
  { flush: 'post', immediate: true }
);

onBeforeUnmount(() => {
  cleanUpExistingWebComponent()
})
</script>

<template>
  <BankingWrapper>
    <div id="regprotect" ref="regProtectContainerRef" class="h-full" />
  </BankingWrapper>
</template>
